<script setup lang="ts">
// One Map Review table: server-paged rows, windowed in this component's own
// scroll container so 250 loaded rows cost only a screenful of DOM, and the
// 116k-row dataset behind them costs nothing here.
//
// Chrome that the table itself must guarantee:
//  - sticky header (label row, plus the collapsible typed filter row) and a
//    sticky identifier first column, so the row's identity stays put sideways
//    and the controls stay reachable downwards;
//  - a horizontally cut-off rightmost column as the "there is more" affordance
//    (the registry keeps columns out of the DOM after dropping them by prio).
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { apiClient } from "@/client";
import LocalizedNumber from "@/components/LocalizedNumber.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import Tooltip from "@/components/Tooltip.vue";
import { formatDateOnly, formatDateOnlyLong } from "@/format";
import { catalogTag } from "@/lib/symbology";
import { symbologyCatalog } from "@/lib/symbologyCatalog";
import { useStoreMapReview } from "@/store/map-review";
import type {
	MapReviewAppliedRate,
	MapReviewColumnFilter,
	MapReviewColumnSummary,
	MapReviewFeatureRow,
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewRateVerdict,
	MapReviewRow,
	MapReviewSort,
	MapReviewTable,
	MapReviewTreatmentRow,
} from "@/type/api";

import DateRangeFilter from "./DateRangeFilter.vue";
import EnumFilter from "./EnumFilter.vue";
import NumberRangeFilter from "./NumberRangeFilter.vue";
import { useColumnOptions } from "./useColumnOptions";
import {
	MAP_REVIEW_COLUMNS,
	MAP_REVIEW_TABLE_LABELS,
	RATE_OVER_COLOR,
	RATE_UNDER_COLOR,
	RELATION_COLUMN,
	type MapReviewColumn,
	type MapReviewColumnOption,
	computeWindowRange,
	clientZoneOffsetMinutes,
	fitColumns,
	parseCellDate,
	revisitDateToInstant,
	summaryLines,
} from "./columns";

interface Props {
	rows: MapReviewRow[];
	table: MapReviewTable;
	total: number;
	loading: boolean;
	selection: { table: MapReviewTable; id: number } | null;
	filters: MapReviewColumnFilter[];
	sort: MapReviewSort | null;
	dense?: boolean;
	/** Whether the typed filter row under the labels is expanded. */
	filtersOpen?: boolean;
	/** The requested table's per-column aggregate over the WHOLE filtered set,
	 *  keyed by column key, straight off the query response. It is read as a
	 *  header tooltip; a column missing from it carries no tooltip rather than
	 *  "0", because "no aggregate here" and "the aggregate is 0" differ. */
	summary?: Record<string, MapReviewColumnSummary>;
}
interface Emits {
	select: [id: number];
	sort: [sort: MapReviewSort | null];
	filter: [filter: MapReviewColumnFilter | null, column: string];
	/** A relation-link cell was activated: navigate + narrow to those records. */
	follow: [filter: MapReviewColumnFilter];
	/** A zone-link cell was activated: narrow the target table to this zone. */
	zoneLink: [link: { table: MapReviewTable; id: number }];
}

const props = withDefaults(defineProps<Props>(), {
	dense: false,
	filtersOpen: true,
	summary: () => ({}),
});
const emit = defineEmits<Emits>();

const ROW_HEIGHT_DENSE = 28;
const ROW_HEIGHT_COMFORT = 34;
const LABEL_ROW_HEIGHT = 28;
const FILTER_ROW_HEIGHT = 30;
const OVERSCAN_ROWS = 8;
const EMPTY_CELL = "—";
/** The tick column's width. Its own column, ahead of the identifier, so a row's
 * inclusion in a bulk write is stated without stealing the identity cell's
 * sticky place. */
const CHECK_COL_WIDTH = 32;

const store = useStoreMapReview();
const { optionsFor } = useColumnOptions();

/** The failed round as the ledger states it. A transport abort is named with
 * the deadline it hit; a server refusal has its own message, so it is shown
 * rather than being reported as a timeout. */
const queryFailure = computed(() => {
	if (store.loadState !== "failed") return "";
	const seconds = store.timedOutSeconds;
	if (seconds === null) return store.error || "The query failed.";
	return `The query took longer than ${seconds} seconds, so we gave up.`;
});

// The inline pin editor's state. The draft is keyed by row id so a reload that
// replaces props.rows wholesale cannot carry a day typed for one Feature onto
// another; pinBusy holds the id of the row whose set/clear is in flight.
const pinDraft = ref<Record<number, string>>({});
const pinBusy = ref<number | null>(null);

const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

const containerEl = ref<HTMLElement | null>(null);
const scrollTop = ref(0);
const containerWidth = ref(0);
const containerHeight = ref(0);
let scrollFrame = 0;
let resizeObserver: ResizeObserver | null = null;

const rowHeight = computed(() =>
	props.dense ? ROW_HEIGHT_DENSE : ROW_HEIGHT_COMFORT,
);

// The sticky header the row window must clear is the label row alone when the
// filter row is collapsed. Feeding the live height into the window keeps the
// top spacer aligned with the rows actually under the header.
const headerHeight = computed(
	() => LABEL_ROW_HEIGHT + (props.filtersOpen ? FILTER_ROW_HEIGHT : 0),
);

const visibleColumns = computed<MapReviewColumn[]>(() =>
	fitColumns(
		MAP_REVIEW_COLUMNS[props.table],
		containerWidth.value - CHECK_COL_WIDTH,
	),
);
const tableWidth = computed(
	() =>
		visibleColumns.value.reduce((sum, c) => sum + c.width, 0) + CHECK_COL_WIDTH,
);

/** The header tick's three states. A full all-matching selection is checked;
 * any explicit rows or unticked exclusions are the indeterminate "some of the
 * matching set", not a page-scoped count. */
const allMatchingChecked = computed(
	() =>
		store.ledgerSelection.all && store.ledgerSelection.excluded.length === 0,
);
const someChecked = computed(() => {
	const selection = store.ledgerSelection;
	return selection.all
		? selection.excluded.length > 0
		: selection.ids.length > 0;
});
const cssVars = computed<Record<string, string>>(() => ({
	"--dt-row-h": `${rowHeight.value}px`,
	"--rate-over": RATE_OVER_COLOR,
	"--rate-under": RATE_UNDER_COLOR,
}));

// The row list starts below the sticky header inside the scroll content, so
// the window is computed from the scroll position past the header.
const range = computed(() =>
	computeWindowRange(
		props.rows.length,
		Math.max(0, scrollTop.value - headerHeight.value),
		containerHeight.value,
		rowHeight.value,
		OVERSCAN_ROWS,
	),
);
const windowRows = computed(() =>
	props.rows.slice(range.value.start, range.value.end),
);
const bottomSpacer = computed(
	() => Math.max(0, props.rows.length - range.value.end) * rowHeight.value,
);

function onScroll(): void {
	if (scrollFrame) return;
	scrollFrame = requestAnimationFrame(() => {
		scrollFrame = 0;
		scrollTop.value = containerEl.value?.scrollTop ?? 0;
	});
}

function resetScroll(): void {
	scrollTop.value = 0;
	if (containerEl.value) containerEl.value.scrollTop = 0;
}

onMounted(() => {
	const el = containerEl.value;
	if (!el) return;
	containerWidth.value = el.clientWidth;
	containerHeight.value = el.clientHeight;
	resizeObserver = new ResizeObserver(() => {
		containerWidth.value = el.clientWidth;
		containerHeight.value = el.clientHeight;
	});
	resizeObserver.observe(el);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	resizeObserver = null;
	if (scrollFrame) cancelAnimationFrame(scrollFrame);
});

// A new page or a new table starts at the top: rows the user was looking at
// are gone, so holding the scroll position would look like a jump into the
// middle of unrelated data.
watch(
	() => [props.table, props.rows] as const,
	() => resetScroll(),
);

function cell(row: MapReviewRow, column: MapReviewColumn): unknown {
	return (row as unknown as Record<string, unknown>)[column.key] ?? null;
}

/**
 * The employee id a ledger person cell can link to, or null when there is
 * none. The Observations ledger's Technician row carries `creator_id` beside
 * the joined name, so its cell links to that employee's page; the Treatments
 * ledger's Applicator row carries the name alone (no id on the wire), so it
 * stays plain text until a resolver exposes the id.
 */
function personUserID(
	row: MapReviewRow,
	column: MapReviewColumn,
): number | null {
	if (column.key !== "creator") return null;
	const name = cell(row, column);
	if (name === null || name === undefined || name === "") return null;
	const id = (row as { creator_id?: number | null }).creator_id;
	return typeof id === "number" ? id : null;
}

function cellText(row: MapReviewRow, column: MapReviewColumn): string {
	const value = cell(row, column);
	if (value === null || value === "" || value === undefined) {
		// A column may name a fallback for an empty cell — the Observations
		// ledger's Technician shows the source's zone where no person was named.
		const fallback = column.valueFallback;
		if (fallback) {
			const alternative = (row as unknown as Record<string, unknown>)[
				fallback.key
			];
			if (
				alternative !== null &&
				alternative !== "" &&
				alternative !== undefined
			) {
				return `${fallback.prefix ?? ""}${String(alternative)}`;
			}
		}
		return EMPTY_CELL;
	}
	if (typeof value === "number") return value.toLocaleString();
	if (column.kind === "enum" && column.valueLabels) {
		return column.valueLabels[String(value)] ?? String(value);
	}
	return String(value);
}

/** One Site's tags as the chips its cell draws — the name and identity colour
 * of each tag the catalog carries for the row's `tag_ids`.
 *
 * Null means "draw the cell's text instead", and it has three causes: the
 * catalog has not loaded (the page reads it beside the ledger, so a fast first
 * page can arrive before it), the row names no tags at all (an empty cell is
 * the text fallback's job, not a row of no chips), or one of its ids is not in
 * the catalog — a tag retired since the row was read, whose assignment the
 * server drops from the catalog's list. In every case the plain `tags` text is
 * still right, because it is the server's own rendering of the same row.
 */
function tagChips(
	row: MapReviewRow,
): { id: number; name: string; color: string }[] | null {
	const catalog = symbologyCatalog.value;
	if (catalog === null) return null;
	const ids = (row as MapReviewSiteRow).tag_ids;
	if (!Array.isArray(ids) || ids.length === 0) return null;
	const chips: { id: number; name: string; color: string }[] = [];
	for (const id of ids) {
		const tag = catalogTag(catalog, id);
		if (!tag) return null;
		chips.push({ id, name: tag.name, color: tag.color });
	}
	return chips;
}

/** The chips of every row on the page, resolved once per render. The cell reads
 * its row's entry twice (the `v-if` and the list), and a lookup per read would
 * walk the catalog's tag list again for every visible row. */
const tagChipsByRow = computed<
	Map<number, { id: number; name: string; color: string }[] | null>
>(() => {
	const chips = new Map<
		number,
		{ id: number; name: string; color: string }[] | null
	>();
	for (const row of props.rows) chips.set(row.id, tagChips(row));
	return chips;
});

/**
 * The treated extent as the server projected it into the district's area unit
 * (area_treated_preferred / area_treated_unit), falling back to the stored
 * square-metre value when a row carries no projection. A row that recorded no
 * area renders the empty cell.
 */
function areaText(row: MapReviewRow): string {
	const treatment = row as MapReviewTreatmentRow;
	if (treatment.area_treated_preferred != null) {
		const value = treatment.area_treated_preferred.toLocaleString();
		return treatment.area_treated_unit
			? `${value} ${treatment.area_treated_unit}`
			: value;
	}
	if (treatment.area_treated != null) {
		return `${treatment.area_treated.toLocaleString()} m²`;
	}
	return EMPTY_CELL;
}

/**
 * The header label for a column. The area column names the district's resolved
 * area unit — read from the rows, which the server renders in it — so the header
 * reads "Area (sq ft)" for a district working in square feet rather than pinning
 * a unit it does not use. Every other column keeps its static label.
 */
function columnLabel(column: MapReviewColumn): string {
	if (column.kind !== "area") return column.label;
	for (const row of props.rows) {
		const unit = (row as MapReviewTreatmentRow).area_treated_unit;
		if (unit) return `${column.label} (${unit})`;
	}
	return column.label;
}

/**
 * A Feature row's PLANNED revisit date (the manual pin) as the server sent it,
 * or null. The server has already applied the supersede rule — a date set on
 * the Feature but answered by a later Treatment arrives as null — so the cell
 * renders the value and never re-derives it.
 */
function plannedRevisit(row: MapReviewRow): string | null {
	return (row as MapReviewFeatureRow).planned_revisit_at;
}

function onPinInput(row: MapReviewRow, event: Event): void {
	pinDraft.value[row.id] = (event.target as HTMLInputElement).value;
}

/**
 * Sets (value) or clears (null) ONE Feature's manual revisit date, then reloads
 * the page so the row shows the server's answer. The bulk actions live in the
 * toolbar and resolve the whole spec; this per-row control says exactly the
 * Feature whose cell was clicked, which the spec-based bulk endpoint cannot
 * express, so it uses the Feature's own endpoint. A failure leaves the row as it
 * was and surfaces through this table's toast.
 */
async function submitRevisit(
	row: MapReviewRow,
	value: string | null,
): Promise<void> {
	if (props.table !== "feature" || pinBusy.value !== null) return;
	pinBusy.value = row.id;
	try {
		await apiClient.JSONPut(`/api/feature/${row.id}`, { revisit_at: value });
		delete pinDraft.value[row.id];
		await store.load();
	} catch (e: any) {
		toastTitle.value = "Revisit date failed";
		toastMessage.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to save the revisit date.";
		toastShow.value = true;
	} finally {
		pinBusy.value = null;
	}
}

function setPin(row: MapReviewRow): void {
	const day = pinDraft.value[row.id];
	if (!day) return;
	void submitRevisit(row, revisitDateToInstant(day));
}

function clearPin(row: MapReviewRow): void {
	void submitRevisit(row, null);
}

/** The district product a treatment row applies, or null when it applied none. */
function districtProductID(row: MapReviewRow): number | null {
	return (row as MapReviewTreatmentRow).product_id ?? null;
}

/**
 * A treatment's prescribed rates grouped by their own unit and basis. The
 * grouping key is the whole basis, so "per acre", "per gallon of water" and a
 * count basis never merge into one row; within a basis the cell shows the span
 * across its scenarios.
 */
function rateGroups(
	row: MapReviewRow,
): { low: number | null; high: number | null; unit: string; basis: string }[] {
	const rates = (row as MapReviewTreatmentRow).prescribed_rates ?? [];
	const groups = new Map<
		string,
		{ low: number | null; high: number | null; unit: string; basis: string }
	>();
	for (const rate of rates) {
		const low = rate.low ?? rate.high;
		const high = rate.high ?? rate.low;
		const key = `${rate.unit}|${rate.basis}`;
		const group = groups.get(key);
		if (group) {
			if (low !== null) {
				group.low = group.low === null ? low : Math.min(group.low, low);
			}
			if (high !== null) {
				group.high = group.high === null ? high : Math.max(group.high, high);
			}
			continue;
		}
		groups.set(key, { low, high, unit: rate.unit, basis: rate.basis });
	}
	return [...groups.values()];
}

function formatRate(
	low: number | null,
	high: number | null,
	unit: string,
	basis: string,
): string {
	const bounds =
		low === null
			? ""
			: high === null || high === low
				? `${low}`
				: `${low}–${high}`;
	return [bounds, unit, basis].filter((part) => part !== "").join(" ");
}

function ratesText(row: MapReviewRow): string {
	const text = rateGroups(row)
		.map((group) => formatRate(group.low, group.high, group.unit, group.basis))
		.filter((part) => part !== "")
		.join("; ");
	return text === "" ? EMPTY_CELL : text;
}

/** The rate tooltip names every scenario, since the cell only shows the span. */
function ratesTooltip(row: MapReviewRow): string {
	const rates = (row as MapReviewTreatmentRow).prescribed_rates ?? [];
	return rates
		.map((rate) =>
			[
				rate.scenario ? `${rate.scenario}:` : "",
				formatRate(rate.low, rate.high, rate.unit, rate.basis),
			]
				.filter((part) => part !== "")
				.join(" "),
		)
		.join("\n");
}

/** The Date column is the Trap Instances surface's only datetime column: the
 * instance's set datetime, which the cell shows with the collect datetime and
 * the derived interval inline. */
function isTrapInstanceDate(column: MapReviewColumn): boolean {
	return props.table === "trap_instance" && column.key === "date";
}

function instanceCollected(row: MapReviewRow): string | null {
	return (row as MapReviewTrapInstanceRow).collected_at;
}

function instanceInterval(row: MapReviewRow): number | null {
	return (row as MapReviewTrapInstanceRow).interval;
}

/** The counts cell: the catch's plain total and its female total, or the reason
 * there is no reading. Display-only — the catch's dimensions are the Mosquito
 * Counts table's own columns, and the full species × sex matrix is in the row's
 * rail detail. */
function countsText(row: MapReviewRow): string {
	const counts = (row as MapReviewTrapInstanceRow).counts;
	if (!counts || counts.state === "not_collected") return "Not collected";
	if (counts.state === "not_analyzed") return "Not analyzed";
	return `${counts.total.toLocaleString()} · ♀${counts.female.toLocaleString()}`;
}

/** The counts tooltip: the catch's totals and its full species × sex matrix,
 * with the vocabulary's full names. */
function countsTooltip(row: MapReviewRow): string {
	const counts = (row as MapReviewTrapInstanceRow).counts;
	if (!counts || counts.state !== "analyzed") return countsText(row);
	const lines = [
		`${counts.total.toLocaleString()} · ♀${counts.female.toLocaleString()}`,
		"",
	];
	if (counts.matrix.length === 0) {
		lines.push("Analyzed: no species were caught");
	} else {
		for (const entry of counts.matrix) {
			lines.push(
				`${entry.species} · ${entry.sex}: ${entry.count.toLocaleString()}`,
			);
		}
	}
	return lines.join("\n");
}

function appliedRate(row: MapReviewRow): MapReviewAppliedRate | null {
	return (row as MapReviewTreatmentRow).applied_rate ?? null;
}

function rateVerdict(row: MapReviewRow): MapReviewRateVerdict {
	return (row as MapReviewTreatmentRow).rate_verdict ?? "unknown";
}

/** The applied rate as value + unit + basis ("0.42 lb per acre"). A coverage
 * basis states a count, not a rate, so it shows the count alone — its cell
 * carries the coverage marker instead of a denominator. */
function formatApplied(rate: MapReviewAppliedRate): string {
	if (rate.coverage) {
		return [rate.value == null ? "" : rate.value.toLocaleString(), rate.unit]
			.filter((part) => part !== "")
			.join(" ");
	}
	return [
		rate.value == null ? "" : rate.value.toLocaleString(),
		rate.unit,
		rate.basis,
	]
		.filter((part) => part !== "")
		.join(" ");
}

/** The coverage marker: the label's own "per N" read as what one unit treats
 * ("up to 135 sq ft"), so the count beside it is not read as a per-area rate.
 * Empty for a proportional rate. */
function coverageCue(rate: MapReviewAppliedRate): string {
	if (!rate.coverage || rate.basis === "") return "";
	const area = rate.basis.startsWith("per ") ? rate.basis.slice(4) : rate.basis;
	return `up to ${area}`;
}

/** The non-colour cue on the rate cell. Empty for in-range/unknown. */
function verdictCue(row: MapReviewRow): string {
	const verdict = rateVerdict(row);
	if (verdict === "over") return "Over label";
	if (verdict === "under") return "Under label";
	return "";
}

const APPLIED_GAP_TITLE =
	"No applied rate: none of the product label's bases is computable from this treatment's area, quantity and feature.";

/** The gap marker is keyed on the verdict, not on the value-less rate: a
 * treatment with no label to check is not the same as one whose label cannot be
 * computed, and the cell says which. Both still read as a gap, never a zero. */
function gapText(row: MapReviewRow): string {
	return rateVerdict(row) === "not_applicable" ? "Not applicable" : "No basis";
}

/** Why there is no applied rate, in the operator's terms: a treatment that
 * applies no product has no label to check at all, and one whose product states
 * no rate has a label with nothing to compare. */
function gapTitle(row: MapReviewRow): string {
	if (rateVerdict(row) !== "not_applicable") return APPLIED_GAP_TITLE;
	const product = (row as MapReviewTreatmentRow).product ?? "";
	return product === ""
		? "No label to check: this treatment applies no product."
		: `No label to check: ${product}'s label states no rate.`;
}

function cellTitle(row: MapReviewRow, column: MapReviewColumn): string {
	if (column.kind === "area") return areaText(row);
	if (column.kind === "counts") return countsTooltip(row);
	if (column.kind === "rates") return ratesTooltip(row) || ratesText(row);
	if (column.kind === "appliedRate") {
		const rate = appliedRate(row);
		if (!rate) return gapTitle(row);
		const verdict = rateVerdict(row);
		const suffix =
			verdict === "over"
				? " — over label"
				: verdict === "under"
					? " — under label"
					: "";
		// The coverage cell shows the count alone, so its tooltip carries the
		// label's own statement in full.
		const coverage = rate.coverage
			? ` — ${coverageCue(rate)} (label basis ${rate.basis})`
			: "";
		return formatApplied(rate) + coverage + suffix;
	}
	// A date column is a bare calendar day: the cell shows the short date, so
	// the tooltip spells the day out with its weekday rather than echoing the
	// raw wire instant. Both read the value through parseCellDate, so a bare
	// "YYYY-MM-DD" means the local day it states and an instant (the planned
	// revisit date) means the instant it states. An absent day carries no title.
	if (column.kind === "date") {
		const value = cell(row, column);
		if (value === null || value === undefined || value === "") return "";
		return formatDateOnlyLong(parseCellDate(String(value)));
	}
	return String(cell(row, column) ?? "");
}

// A relation-link cell narrows the related table to exactly this row's related
// records: the clause carries the source table and this row's id, and the
// server resolves it like any other filter.
function followColumn(row: MapReviewRow, column: MapReviewColumn): void {
	if (!column.relation) return;
	emit("follow", {
		table: column.relation.target,
		column: RELATION_COLUMN,
		op: "relation",
		source: props.table,
		source_id: row.id,
	});
}

// A zone-link cell narrows the target table to the records this zone holds. A
// zone has no relation clause to emit — membership is geometric, computed by
// the server — so the narrowing is the spec's own zone list, set to this one
// row's id by the view that holds the store.
function followZoneLink(row: MapReviewRow, column: MapReviewColumn): void {
	if (!column.zoneLink) return;
	emit("zoneLink", { table: column.zoneLink.target, id: row.id });
}

function currentFilter(
	column: MapReviewColumn,
): MapReviewColumnFilter | undefined {
	return props.filters.find(
		(f) => f.table === props.table && f.column === column.key,
	);
}

function isSelected(row: MapReviewRow): boolean {
	const selection = props.selection;
	return (
		selection !== null &&
		selection.table === props.table &&
		selection.id === row.id
	);
}

function toggleSort(column: MapReviewColumn): void {
	if (!column.sortable) return;
	const current = props.sort;
	if (current === null || current.column !== column.key) {
		emit("sort", { column: column.key, desc: false });
		return;
	}
	// asc -> desc -> the table's default sort.
	emit("sort", current.desc ? null : { column: column.key, desc: true });
}

// Filter controls emit whole clauses (or null to clear) and never hold local
// state: the store debounces the text-carrying ones.
function onContains(column: MapReviewColumn, event: Event): void {
	const text = (event.target as HTMLInputElement).value;
	emit(
		"filter",
		text.trim() === ""
			? null
			: { table: props.table, column: column.key, op: "contains", text },
		column.key,
	);
}

// The range popout hands back the bounds it holds, or null when cleared; the
// clause is built here so the wire shape is unchanged from the two inline
// fields it replaced.
function onNumberRangeFilter(
	column: MapReviewColumn,
	range: { min: number | null; max: number | null } | null,
): void {
	emit(
		"filter",
		range === null
			? null
			: {
					table: props.table,
					column: column.key,
					op: "numberRange",
					min: range.min,
					max: range.max,
				},
		column.key,
	);
}

// The date cell's popout hands back the range it was given, or null when it was
// cleared; the clause is built here, so every dateRange clause carries the
// viewer's zone and the server reads the days as the viewer's days.
function onDateRangeFilter(
	column: MapReviewColumn,
	range: { from: string | null; to: string | null } | null,
): void {
	emit(
		"filter",
		range === null
			? null
			: {
					table: props.table,
					column: column.key,
					op: "dateRange",
					from: range.from,
					to: range.to,
					tz_offset_minutes: clientZoneOffsetMinutes(),
				},
		column.key,
	);
}

// The enum control emits the whole new value list (empty = cleared); this is
// the one place an enum clause is built, so every enum column shares the path.
function onEnum(column: MapReviewColumn, values: (string | number)[]): void {
	emit(
		"filter",
		values.length === 0
			? null
			: { table: props.table, column: column.key, op: "enum", values },
		column.key,
	);
}

// One column's aggregate over the whole filtered set, keyed by column. It rides
// on the column label as a tooltip now that the summary footer is gone: the
// reading is still there on hover, without a second footer's height or a cell
// too narrow to read it.
function headerSummary(column: MapReviewColumn): string {
	return summaryLines(column, props.summary[column.key]).join(" · ");
}
</script>

<template>
	<div class="dt-root">
		<div
			ref="containerEl"
			class="dt-scroll"
			:style="cssVars"
			@scroll="onScroll"
		>
			<table
				class="dt-table"
				:style="{ width: `${tableWidth}px`, minWidth: '100%' }"
				:aria-rowcount="total"
			>
				<colgroup>
					<col :style="{ width: `${CHECK_COL_WIDTH}px` }" />
					<col
						v-for="column in visibleColumns"
						:key="column.key"
						:style="{ width: `${column.width}px` }"
					/>
				</colgroup>
				<thead>
					<tr class="dt-labels">
						<th class="dt-th dt-check dt-sticky-col">
							<input
								type="checkbox"
								class="dt-check-input"
								:checked="allMatchingChecked"
								:indeterminate="someChecked"
								:disabled="total === 0"
								:aria-label="
									allMatchingChecked
										? 'Deselect all rows'
										: 'Select every row matching the current filter'
								"
								:title="
									allMatchingChecked
										? 'Deselect all rows'
										: 'Select every row matching the current filter'
								"
								@change="store.toggleSelectAllMatching(table)"
							/>
						</th>
						<th
							v-for="(column, index) in visibleColumns"
							:key="column.key"
							class="dt-th"
							:class="{
								'dt-sticky-col': index === 0,
								'dt-sticky-offset': index === 0,
								'dt-num': column.align === 'right',
								'dt-sortable': column.sortable,
							}"
							:aria-sort="
								sort?.column === column.key
									? sort.desc
										? 'descending'
										: 'ascending'
									: undefined
							"
							:tabindex="column.sortable ? 0 : undefined"
							@click="toggleSort(column)"
							@keydown.enter="toggleSort(column)"
						>
							<Tooltip
								v-if="headerSummary(column)"
								placement="top"
								:title="headerSummary(column)"
							>
								<span class="dt-th-label">{{ columnLabel(column) }}</span>
							</Tooltip>
							<span v-else class="dt-th-label">
								{{ columnLabel(column) }}
							</span>
							<span
								v-if="sort?.column === column.key"
								class="dt-sort-mark"
								aria-hidden="true"
							>
								{{ sort.desc ? "▾" : "▴" }}
							</span>
						</th>
					</tr>
					<tr v-if="filtersOpen" class="dt-filters">
						<th class="dt-th dt-filter-cell dt-check dt-sticky-col"></th>
						<th
							v-for="(column, index) in visibleColumns"
							:key="column.key"
							class="dt-th dt-filter-cell"
							:class="{
								'dt-sticky-col': index === 0,
								'dt-sticky-offset': index === 0,
							}"
						>
							<input
								v-if="column.filterOps[0] === 'contains'"
								type="search"
								class="dt-filter-input"
								:aria-label="`Filter ${column.label}`"
								:value="currentFilter(column)?.text ?? ''"
								@input="onContains(column, $event)"
							/>
							<NumberRangeFilter
								v-else-if="column.filterOps[0] === 'numberRange'"
								:table="table"
								:column="column"
								:min="currentFilter(column)?.min ?? null"
								:max="currentFilter(column)?.max ?? null"
								@change="onNumberRangeFilter(column, $event)"
							/>
							<DateRangeFilter
								v-else-if="column.filterOps[0] === 'dateRange'"
								:column="column"
								:from="currentFilter(column)?.from ?? ''"
								:to="currentFilter(column)?.to ?? ''"
								@change="onDateRangeFilter(column, $event)"
							/>
							<EnumFilter
								v-else-if="column.filterOps[0] === 'enum'"
								:column="column"
								:options="optionsFor(column)"
								:values="currentFilter(column)?.values ?? []"
								@change="onEnum(column, $event)"
							/>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-if="range.offset > 0"
						class="dt-spacer"
						:style="{ height: `${range.offset}px` }"
					>
						<td :colspan="visibleColumns.length + 1"></td>
					</tr>
					<tr
						v-for="row in windowRows"
						:key="row.id"
						class="dt-row"
						:class="{
							'dt-selected': isSelected(row),
							'dt-row--over': rateVerdict(row) === 'over',
							'dt-row--under': rateVerdict(row) === 'under',
						}"
						tabindex="0"
						@click="emit('select', row.id)"
						@keydown.enter="emit('select', row.id)"
					>
						<!-- The tick states this row's inclusion in a bulk write;
						     it never opens the record (that is the row click), so
						     its own click stops before the row's. -->
						<td class="dt-td dt-check dt-sticky-col">
							<input
								type="checkbox"
								class="dt-check-input"
								:checked="store.isRowChecked(table, row.id)"
								:aria-label="`Select ${MAP_REVIEW_TABLE_LABELS[table]} row ${row.id}`"
								@click.stop
								@keydown.stop
								@change="store.toggleRowChecked(table, row.id)"
							/>
						</td>
						<td
							v-for="(column, index) in visibleColumns"
							:key="column.key"
							class="dt-td"
							:class="{
								'dt-sticky-col': index === 0,
								'dt-sticky-offset': index === 0,
								'dt-num': column.align === 'right',
								'dt-null': cellText(row, column) === EMPTY_CELL,
							}"
							:title="cellTitle(row, column)"
						>
							<button
								v-if="column.relation"
								type="button"
								class="dt-link"
								:title="`Show ${column.label} for this row`"
								:aria-label="`Show ${column.label} for this row`"
								@click.stop="followColumn(row, column)"
								@keydown.enter.stop
							>
								{{ cellText(row, column) }}
							</button>
							<!-- The zone link, the relation link's sibling: the row
							     is a zone, which has no relation to narrow by, so
							     the cell hands its id to the view and the view
							     narrows the target ledger with the spec's own zone
							     list. The button and its title read like the
							     relation link, so the cell needs no new styling. -->
							<button
								v-else-if="column.zoneLink"
								type="button"
								class="dt-link"
								:title="`Show ${MAP_REVIEW_TABLE_LABELS[column.zoneLink.target]} in this zone`"
								:aria-label="`Show ${MAP_REVIEW_TABLE_LABELS[column.zoneLink.target]} in this zone`"
								@click.stop="followZoneLink(row, column)"
								@keydown.enter.stop
							>
								{{ cellText(row, column) }}
							</button>
							<RouterLink
								v-else-if="
									column.kind === 'productLink' &&
									districtProductID(row) != null
								"
								:to="`/_/inventory/products/${districtProductID(row)}`"
								class="dt-link"
								:title="`Open ${cellText(row, column)} in your district's products`"
								@click.stop
							>
								{{ cellText(row, column) }}
							</RouterLink>
							<RouterLink
								v-else-if="
									column.kind === 'enum' && personUserID(row, column) != null
								"
								:to="`/_/configuration/user/${personUserID(row, column)}`"
								class="dt-link"
								:title="`Open ${cellText(row, column)}'s employee page (id ${personUserID(row, column)})`"
								@click.stop
							>
								{{ cellText(row, column) }}
							</RouterLink>
							<span
								v-else-if="column.kind === 'appliedRate'"
								class="dt-applied"
							>
								<template v-if="appliedRate(row)">
									<span class="dt-applied-value">{{
										formatApplied(appliedRate(row)!)
									}}</span>
									<span
										v-if="coverageCue(appliedRate(row)!)"
										class="dt-coverage-cue"
										>{{ coverageCue(appliedRate(row)!) }}</span
									>
									<span v-if="verdictCue(row)" class="dt-verdict-cue">{{
										verdictCue(row)
									}}</span>
								</template>
								<span v-else class="dt-rate-gap" :title="gapTitle(row)">{{
									gapText(row)
								}}</span>
							</span>
							<span v-else-if="column.kind === 'area'" class="dt-area">
								{{ areaText(row) }}
							</span>
							<span
								v-else-if="column.kind === 'rates'"
								class="dt-rates"
								:title="ratesTooltip(row)"
							>
								{{ ratesText(row) }}
							</span>
							<span
								v-else-if="column.kind === 'counts'"
								class="dt-counts"
								:title="countsTooltip(row)"
							>
								{{ countsText(row) }}
							</span>
							<span
								v-else-if="
									column.kind === 'date' && column.key === 'planned_revisit_at'
								"
								class="dt-pin"
							>
								<template v-if="plannedRevisit(row)">
									<i
										class="bi bi-pin-angle-fill dt-pin-icon"
										aria-hidden="true"
									></i>
									<span class="dt-date">{{
										formatDateOnly(new Date(plannedRevisit(row)!))
									}}</span>
									<button
										type="button"
										class="dt-pin-btn"
										:disabled="pinBusy === row.id"
										:title="`Clear the planned revisit date for feature ${row.id}`"
										@click.stop="clearPin(row)"
										@keydown.enter.stop
									>
										Clear
									</button>
								</template>
								<template v-else>
									<span class="dt-pin-none">{{ EMPTY_CELL }}</span>
									<input
										type="date"
										class="dt-pin-date"
										:value="pinDraft[row.id] ?? ''"
										:disabled="pinBusy === row.id"
										:aria-label="`Planned revisit date for feature ${row.id}`"
										@click.stop
										@keydown.enter.stop
										@input="onPinInput(row, $event)"
									/>
									<button
										type="button"
										class="dt-pin-btn"
										:disabled="pinBusy === row.id || !pinDraft[row.id]"
										:title="`Set the planned revisit date for feature ${row.id}`"
										@click.stop="setPin(row)"
										@keydown.enter.stop
									>
										Set
									</button>
								</template>
							</span>
							<span
								v-else-if="column.kind === 'date' && cell(row, column)"
								class="dt-date"
							>
								{{ formatDateOnly(parseCellDate(String(cell(row, column)))) }}
							</span>
							<span
								v-else-if="column.kind === 'datetime' && cell(row, column)"
								class="dt-date dt-date-inline"
								:class="{ 'dt-date-seq': isTrapInstanceDate(column) }"
							>
								<template v-if="isTrapInstanceDate(column)">
									<TimeRelativeWithTooltip :time="String(cell(row, column))" />
									<span class="dt-date-detail">
										<template v-if="instanceCollected(row)">
											→
											<TimeRelativeWithTooltip
												:time="String(instanceCollected(row))"
											/>
										</template>
										<span v-else class="dt-date-open">still out</span>
										<span
											v-if="instanceInterval(row) != null"
											class="dt-date-interval"
										>
											· {{ instanceInterval(row) }}d
										</span>
									</span>
								</template>
								<TimeRelativeWithTooltip
									v-else
									:time="String(cell(row, column))"
								/>
							</span>
							<span
								v-else-if="column.kind === 'bool'"
								class="dt-bool"
								:class="cell(row, column) ? 'dt-yes' : 'dt-no'"
							>
								{{ cell(row, column) ? "Yes" : "No" }}
							</span>
							<LocalizedNumber
								v-else-if="
									column.kind === 'number' &&
									typeof cell(row, column) === 'number'
								"
								:value="cell(row, column) as number"
							/>
							<!-- The Site's tags: one chip per tag, drawn from the row's
							     `tag_ids` and the symbology catalog's own names and
							     colours. The cell falls back to the row's `tags` text
							     whenever the chips cannot be drawn faithfully — no
							     catalog yet, no ids, or an id the catalog no longer
							     carries (a tag retired since the page was read) — so the
							     reading is never narrower than the server's own. -->
							<span v-else-if="column.kind === 'tags'" class="dt-tags">
								<template v-if="tagChipsByRow.get(row.id)">
									<span
										v-for="chip in tagChipsByRow.get(row.id)!"
										:key="chip.id"
										class="dt-tag-chip"
										:title="chip.name"
									>
										<span
											class="dt-tag-dot"
											:style="{ background: chip.color }"
											aria-hidden="true"
										></span>
										<span class="dt-tag-name">{{ chip.name }}</span>
									</span>
								</template>
								<span v-else>{{ cellText(row, column) }}</span>
							</span>
							<span v-else>{{ cellText(row, column) }}</span>
						</td>
					</tr>
					<tr
						v-if="bottomSpacer > 0"
						class="dt-spacer"
						:style="{ height: `${bottomSpacer}px` }"
					>
						<td :colspan="visibleColumns.length + 1"></td>
					</tr>
				</tbody>
			</table>
		</div>

		<div v-if="store.loadState === 'failed'" class="dt-error" role="alert">
			<span class="dt-error-message">{{ queryFailure }}</span>
			<button type="button" class="dt-error-retry" @click="store.load()">
				Retry
			</button>
		</div>

		<div
			v-else-if="
				store.loadState === 'resolved' && !loading && rows.length === 0
			"
			class="dt-empty"
		>
			{{
				total === 0
					? "No rows match the current filters."
					: "No rows on this page."
			}}
		</div>

		<div v-if="loading" class="dt-loading" role="status" aria-live="polite">
			<span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
			<span class="dt-loading-text">Loading…</span>
		</div>

		<ToastNotification
			v-model:show="toastShow"
			:title="toastTitle"
			:message="toastMessage"
		/>
	</div>
</template>

<style scoped>
.dt-root {
	position: relative;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	min-width: 0;
	background: #fff;
}

.dt-scroll {
	flex: 1 1 auto;
	min-height: 0;
	overflow: auto;
	position: relative;
}

.dt-table {
	border-collapse: separate;
	border-spacing: 0;
	table-layout: fixed;
	font-size: 0.75rem;
}

.dt-labels {
	height: 28px;
}

.dt-filters {
	height: 30px;
}

.dt-th,
.dt-td {
	padding: 0 6px;
	box-sizing: border-box;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: clip;
}

.dt-th {
	position: sticky;
	top: 0;
	z-index: 2;
	height: 28px;
	line-height: 28px;
	background: #f1f3f5;
	border-bottom: 1px solid #dee2e6;
	font-weight: 600;
	color: #343a40;
	user-select: none;
}

.dt-sortable {
	cursor: pointer;
}

.dt-sortable:hover {
	background: #e9ecef;
}

.dt-sort-mark {
	margin-left: 3px;
	color: #0d6efd;
}

.dt-filter-cell {
	top: 28px;
	height: 30px;
	line-height: 30px;
	padding: 2px 4px;
	background: #f8f9fa;
}

.dt-filter-input {
	width: 100%;
	height: 22px;
	padding: 0 3px;
	font-size: 0.6875rem;
	border: 1px solid #ced4da;
	border-radius: 2px;
	background: #fff;
}

.dt-row {
	height: var(--dt-row-h);
	cursor: pointer;
}

.dt-td {
	height: var(--dt-row-h);
	line-height: calc(var(--dt-row-h) - 1px);
	box-shadow: inset 0 -1px 0 #eef0f2;
	color: #212529;
	background: #fff;
}

/* Sticky identifier column: the row keeps saying what it is while the rest of
   the registry scrolls past. */
.dt-sticky-col {
	position: sticky;
	left: 0;
}

.dt-th.dt-sticky-col {
	z-index: 3;
}

.dt-td.dt-sticky-col {
	z-index: 1;
	background: #fff;
	box-shadow:
		inset 0 -1px 0 #eef0f2,
		1px 0 0 #e9ecef;
}

/* The tick column is the first sticky column; the identifier follows it, offset
   by the tick's width so both stay put side by side. */
.dt-sticky-offset {
	left: 32px;
}

.dt-check {
	padding: 0;
	text-align: center;
}

.dt-check-input {
	width: 14px;
	height: 14px;
	margin: 0;
	vertical-align: middle;
	cursor: pointer;
}

.dt-row:hover .dt-td {
	background: #f8f9fa;
}

.dt-row:hover .dt-td.dt-sticky-col {
	background: #f8f9fa;
}

.dt-row.dt-selected .dt-td,
.dt-row.dt-selected .dt-td.dt-sticky-col {
	background: #e7f1ff;
}

.dt-num {
	text-align: right;
	font-variant-numeric: tabular-nums;
}

/* Relation-link cell: a real button, so it is keyboard reachable and its
   activation does not select the row. Styled as a link so the affordance reads
   as "go to these records", not "a control in the cell". */
.dt-link {
	border: 0;
	padding: 0;
	background: transparent;
	color: #0d6efd;
	font: inherit;
	text-align: left;
	text-decoration: underline;
	text-underline-offset: 2px;
	cursor: pointer;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dt-num .dt-link {
	text-align: right;
}

.dt-link:hover {
	color: #0a58ca;
}

.dt-link:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: 1px;
}

/* The prescribed-rate cell: the label's scenarios compressed to one line per
   basis, with the full scenario list in the tooltip. */
.dt-rates {
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
}

/* The tags cell: one chip per tag, the tag's own identity colour as a dot and
   its name beside it. The chip is deliberately a neutral pill with a coloured
   dot rather than a block of the tag's colour: a district's tags are arbitrary
   hex values, and white type over a pale one is unreadable. The row is one line
   tall and the column is narrow, so the chips share the width and the last one
   truncates — the cell's tooltip carries the full list. */
.dt-tags {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	max-width: 100%;
	overflow: hidden;
	white-space: nowrap;
	vertical-align: bottom;
}

.dt-tag-chip {
	display: inline-flex;
	align-items: center;
	gap: 3px;
	min-width: 0;
	max-width: 100%;
	padding: 0 6px;
	border: 1px solid #dee2e6;
	border-radius: 999px;
	background: #f1f3f5;
	color: #343a40;
	font-size: 0.75rem;
	line-height: 1.5;
	overflow: hidden;
}

.dt-tag-dot {
	flex: 0 0 auto;
	width: 7px;
	height: 7px;
	border-radius: 50%;
}

.dt-tag-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* The applied-rate cell: the derived rate, the over/under cue and the gap. */
.dt-applied {
	display: inline-flex;
	align-items: baseline;
	gap: 6px;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
}

/* A coverage basis states a count, not a rate: the marker names what one unit
   treats, so the number beside it is not read as a per-area rate. */
.dt-coverage-cue {
	font-size: 0.6875rem;
	color: #6c757d;
	white-space: nowrap;
}

.dt-applied-value {
	overflow: hidden;
	text-overflow: ellipsis;
}

/* The verdict must never be colour alone: the cue spells it out, and the row
   carries a left rule in the same Okabe–Ito colour. */
.dt-verdict-cue {
	font-size: 0.6875rem;
	font-weight: 700;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	white-space: nowrap;
}

.dt-row--over .dt-verdict-cue::before {
	content: "▲ ";
	color: var(--rate-over);
}

.dt-row--under .dt-verdict-cue::before {
	content: "▼ ";
	color: var(--rate-under);
}

.dt-row--over .dt-verdict-cue {
	color: #8a3d00; /* readable text; the Okabe–Ito colour carries the rule */
}

.dt-row--under .dt-verdict-cue {
	color: #00517f;
}

/* Over is a warning, under is informational: same visibility, lower salience. */
.dt-row--over .dt-td.dt-sticky-col {
	box-shadow:
		inset 3px 0 0 var(--rate-over),
		inset 0 -1px 0 #eef0f2,
		1px 0 0 #e9ecef;
}

.dt-row--under .dt-td.dt-sticky-col {
	box-shadow:
		inset 2px 0 0 var(--rate-under),
		inset 0 -1px 0 #eef0f2,
		1px 0 0 #e9ecef;
}

/* A gap reads as "no rate to show", never as a zero: the cell's own text says
   whether there was no label to check or a label whose rate cannot be computed. */
.dt-rate-gap {
	color: #6c757d;
	font-style: italic;
}

.dt-null {
	color: #adb5bd;
}

.dt-bool {
	font-weight: 600;
}

.dt-yes {
	color: #198754;
}

.dt-no {
	color: #adb5bd;
}

/* The pin cell: the pin glyph, its date and the set/clear controls. The date
   input is only as wide as the controls need, and every control is small so the
   row stays a row. */
.dt-pin {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	max-width: 100%;
}

.dt-pin-icon {
	color: #6c757d;
	font-size: 0.75rem;
}

.dt-pin-none {
	color: #adb5bd;
}

.dt-pin-date {
	width: 106px;
	height: 20px;
	padding: 0 2px;
	border: 1px solid #ced4da;
	border-radius: 3px;
	font-size: 0.6875rem;
	background: #fff;
}

.dt-pin-btn {
	border: 1px solid #ced4da;
	border-radius: 3px;
	padding: 0 4px;
	height: 20px;
	background: #f8f9fa;
	color: #495057;
	font-size: 0.6875rem;
	line-height: 1;
}

.dt-pin-btn:hover:not(:disabled) {
	background: #e9ecef;
	color: #0a58ca;
}

.dt-pin-btn:disabled {
	opacity: 0.5;
}

.dt-pin-btn:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: 1px;
}

.dt-spacer td {
	padding: 0;
	border: 0;
	box-shadow: none;
	background: transparent;
}

/* A failed round, in the ledger where the query belongs. It covers the stale
   page (a round is all-or-nothing, so the rows in hand are the previous
   answer), and unlike the empty and loading states it carries a control: the
   retry. */
.dt-error {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 10px;
	padding: 16px;
	background: rgba(255, 255, 255, 0.9);
	color: #495057;
	font-size: 0.8125rem;
	text-align: center;
}

.dt-error-message {
	max-width: 420px;
}

.dt-error-retry {
	padding: 3px 12px;
	border: 1px solid #0d6efd;
	border-radius: 0.25rem;
	background: #0d6efd;
	color: #fff;
	font-size: 0.8125rem;
	cursor: pointer;
}

.dt-error-retry:hover {
	background: #0b5ed7;
}

.dt-empty,
.dt-loading {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	color: #6c757d;
	font-size: 0.8125rem;
	pointer-events: none;
}

.dt-loading {
	background: rgba(255, 255, 255, 0.55);
}
</style>
